import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { curriculumAPI, marketplaceAPI, participantAPI, adaptiveAPI } from '../services/api';
import LearnerNav from '../components/nav/LearnerNav';

/** Convert API completion_pct (0–1 fraction OR 0–100 integer) to a display percentage. */
function displayCompletionPct(raw) {
  const n = Number(raw);
  if (raw == null || Number.isNaN(n)) return 0;
  if (n > 1) return Math.round(Math.min(100, n));
  return Math.round(n * 100);
}

const TRACK_LABELS = {
  ai_fundamentals: 'AI Fundamentals',
  ai_orchestrator_certificate: 'AI Orchestrator Certificate',
  ai_entrepreneur_certificate: 'AI Entrepreneur Certificate',
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const { learner } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashcardTotal, setFlashcardTotal] = useState(0);
  const [flashcardDomain, setFlashcardDomain] = useState(null);
  const [progressMap, setProgressMap] = useState({}); // domain_id → { completion_pct, modules_completed, modules_total }
  // AI consolidation: track_id → per-track progress from progress/summary.per_track_breakdown
  const [trackProgressMap, setTrackProgressMap] = useState({});

  useEffect(() => {
    if (!learner?.learner_id) return;
    adaptiveAPI.getReviewQueue(learner.learner_id)
      .then(({ data }) => {
        setFlashcardTotal(data.total || 0);
        const byDomain = data.by_domain || data.queue || [];
        if (byDomain.length > 0) {
          setFlashcardDomain(byDomain[0].domain || null);
        }
      })
      .catch(() => {});
  }, [learner]);

  useEffect(() => {
    const subsFetch = learner?.learner_id
      ? marketplaceAPI.getSubscriptions(learner.learner_id).then(({ data }) => data.subscriptions || []).catch(() => [])
      : Promise.resolve([]);

    Promise.all([
      curriculumAPI.listDomains().then(({ data }) => data.domains || []).catch(() => []),
      subsFetch,
      curriculumAPI.getProgressSummary().then(({ data }) => data.domains || []).catch(() => []),
    ])
      .then(([doms, subs, progressDomains]) => {
        setDomains(doms);
        setSubscriptions(subs);
        const map = {};
        const trackMap = {};
        progressDomains.forEach((p) => {
          map[p.domain_id] = p;
          (p.per_track_breakdown || []).forEach((t) => {
            if (t.track_id) trackMap[t.track_id] = t;
          });
        });
        setProgressMap(map);
        setTrackProgressMap(trackMap);
      })
      .finally(() => setLoading(false));
  }, [learner]);

  const isActiveSub = (s) => s.status !== 'expired' && s.status !== 'cancelled';

  const isEnrolled = (domainId) =>
    subscriptions.some((s) => s.domain_id === domainId && isActiveSub(s)) ||
    domains.find((d) => d.domain_id === domainId)?.enrolled;

  const handleDomainClick = useCallback(async (domain) => {
    if (!isEnrolled(domain.domain_id)) {
      navigate(`/enroll/${domain.domain_id}`, { state: { listing: domain } });
      return;
    }
    // Check if character archetypes are enabled for this domain.
    // When disabled (the default), skip the character-select page entirely and
    // navigate directly to the domain — reducing unnecessary clicks for learners.
    try {
      const { data } = await participantAPI.getAvailableClasses(domain.domain_id);
      if (!data?.character_archetypes_enabled) {
        // Archetypes disabled — go straight to the domain (no picker needed)
        navigate(`/domain/${domain.domain_id}`);
        return;
      }
    } catch {
      // If the check fails, fall through to character-select (safe default)
    }
    navigate(`/character-select/${domain.domain_id}`);
  }, [isEnrolled, navigate]);

  // AI domain consolidation: split "ai" domain from others for special rendering
  const aiDomain = domains.find((d) => d.domain_id === 'ai');
  const nonAiDomains = domains.filter((d) => d.domain_id !== 'ai');
  const isAiEnrolled = isEnrolled('ai');
  // Prefer active marketplace subscriptions as source of truth for track cards
  // (JWT enrolled_tracks can lag after coach cancels a track).
  const LEGACY_AI_TRACK = {
    ai_beginner: 'ai_fundamentals',
    ai_orchestrator: 'ai_orchestrator_certificate',
    ai_entrepreneur: 'ai_entrepreneur_certificate',
  };
  // Track cards come only from subscriptions / profile.enrolled_tracks
  // (explicit track_id). No client-side "all tracks" default.
  const aiTracksFromSubs = [
    ...new Set(
      subscriptions
        .filter((s) => {
          const did = s.domain_id;
          return isActiveSub(s) && (did === 'ai' || LEGACY_AI_TRACK[did]);
        })
        .map((s) => s.track_id || LEGACY_AI_TRACK[s.domain_id])
        .filter(Boolean),
    ),
  ];
  const aiEnrolledTracks = aiTracksFromSubs.length > 0
    ? aiTracksFromSubs
    : (isAiEnrolled ? (learner?.enrolled_tracks?.ai || []) : []);
  const enrolledNonAiCount = nonAiDomains.filter((d) => isEnrolled(d.domain_id)).length;
  // Count enrolled DOMAINS (not tracks) for the heading: a learner with 3 AI tracks
  // but only the AI domain enrolled should see "Continue Your Training", not "Select a Domain".
  const enrolledDomainCount = enrolledNonAiCount + (aiDomain && isAiEnrolled ? 1 : 0);
  const totalEnrolledItems = enrolledNonAiCount + aiEnrolledTracks.length;

  const renderProgressBar = (pct, prog) => (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11, color: '#6a6a72', marginBottom: 3 }}>
        {pct}% complete
        {prog?.modules_completed != null && prog?.modules_total != null && (
          <span> · {prog.modules_completed}/{prog.modules_total} modules</span>
        )}
      </div>
      <div style={{ height: 4, background: 'rgba(0,0,0,0.08)', borderRadius: 9999, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: pct <= 30 ? '#f59e0b' : pct <= 80 ? '#FFCF99' : '#92140C',
          borderRadius: 9999, transition: 'width 0.3s',
        }} />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0' }}>
      <LearnerNav />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '30px 20px' }}>
      <main className="dashboard-content">
        <h2>
          {enrolledDomainCount <= 1
            ? t('continue_training', { ns: 'curriculum', defaultValue: 'Continue Your Training' })
            : t('select_domain', { ns: 'curriculum' })}
        </h2>

        {loading ? (
          <p>{t('loading')}</p>
        ) : (<>
          {/* Non-AI domains */}
          {nonAiDomains.length > 0 && (
            <div className="domain-grid">
              {nonAiDomains.map((d) => {
                const enrolled = isEnrolled(d.domain_id);
                const prog = progressMap[d.domain_id];
                const pct = prog ? displayCompletionPct(prog.completion_pct) : null;
                const isComplete = pct === 100;
                return (
                  <div
                    key={d.domain_id}
                    className="domain-card"
                    onClick={() => handleDomainClick(d)}
                    style={enrolled && isComplete ? {
                      borderColor: '#92140C',
                      boxShadow: '0 0 0 2px rgba(146,20,12,0.12)',
                    } : {}}
                  >
                    <h3>{d.display_name}</h3>
                    <div className="domain-modes">
                      {d.supported_modes?.map((m) => (
                        <span key={m} className="mode-badge">{m}</span>
                      ))}
                    </div>
                    {enrolled && isComplete && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#92140C', color: '#fff',
                        borderRadius: 9999, padding: '3px 10px',
                        fontSize: 11, fontWeight: 700, marginBottom: 6,
                      }}>
                        ✓ Completed
                      </div>
                    )}
                    {enrolled && pct != null && !isComplete && pct > 0 && renderProgressBar(pct, prog)}
                    <span className={`enroll-badge ${enrolled ? 'enrolled' : ''}`}>
                      {enrolled
                        ? t('enrolled', { ns: 'curriculum' })
                        : t('enroll', { ns: 'curriculum' })
                      }
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* AI domain — enrolled: show per-track cards; unenrolled: single enroll card */}
          {aiDomain && (
            <>
              {isAiEnrolled && aiEnrolledTracks.length > 0 ? (
                <>
                  {aiEnrolledTracks.length > 1 && (
                    <h3 style={{
                      marginTop: 24, marginBottom: 12,
                      fontSize: '1rem', fontWeight: 700, color: '#1E1E24', letterSpacing: '0.03em',
                    }}>
                      AI
                    </h3>
                  )}
                  <div className="domain-grid">
                    {aiEnrolledTracks.map((trackId) => {
                      // Per-track progress (8–9 modules), not domain-wide (26).
                      const prog = trackProgressMap[trackId] || null;
                      const pct = prog ? displayCompletionPct(prog.completion_pct) : null;
                      const isComplete = pct === 100;
                      return (
                        <div
                          key={trackId}
                          className="domain-card"
                          onClick={() => navigate(`/domain/ai?track=${trackId}`)}
                          style={isComplete ? {
                            borderColor: '#92140C',
                            boxShadow: '0 0 0 2px rgba(146,20,12,0.12)',
                          } : {}}
                        >
                          <h3>{TRACK_LABELS[trackId] || (trackId ? trackId.replace(/_/g, ' ') : 'AI Track')}</h3>
                          {isComplete && (
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: '#92140C', color: '#fff',
                              borderRadius: 9999, padding: '3px 10px',
                              fontSize: 11, fontWeight: 700, marginBottom: 6,
                            }}>
                              ✓ Completed
                            </div>
                          )}
                          {pct != null && !isComplete && pct > 0 && renderProgressBar(pct, prog)}
                          <span className="enroll-badge enrolled">
                            {t('enrolled', { ns: 'curriculum' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : !isAiEnrolled ? (
                <div className="domain-grid">
                  <div
                    className="domain-card"
                    onClick={() => handleDomainClick(aiDomain)}
                  >
                    <h3>{aiDomain.display_name}</h3>
                    <div className="domain-modes">
                      {aiDomain.supported_modes?.map((m) => (
                        <span key={m} className="mode-badge">{m}</span>
                      ))}
                    </div>
                    <span className="enroll-badge">
                      {t('enroll', { ns: 'curriculum' })}
                    </span>
                  </div>
                </div>
              ) : null}
            </>
          )}
          {flashcardTotal > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #1E1E24 0%, #2d2d35 100%)',
              borderRadius: 12, padding: '16px 20px', marginTop: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              color: '#FFF8F0', border: '1px solid #FFCF99',
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  {flashcardTotal} flashcard{flashcardTotal !== 1 ? 's' : ''} ready for review
                </div>
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2, color: '#FFCF99' }}>
                  Keep your knowledge sharp — review your cards now
                </div>
              </div>
              <button
                onClick={() => navigate(flashcardDomain ? `/flashcards/${flashcardDomain}` : '/flashcards')}
                style={{
                  background: '#92140C', border: '1px solid rgba(255,207,153,0.4)',
                  color: '#FFF8F0', borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                  fontWeight: 600, fontSize: 14,
                }}
              >
                Review Now →
              </button>
            </div>
          )}
        </>
        )}
      </main>
      </div>
    </div>
  );
}
